import { MenuItem } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import type { RenderFilterProps } from "@shared/frontend/hyper-table/filter-types";
import { useHyperTable } from "@shared/frontend/hyper-table/HyperTable";
import { generateHyperFilterFromPath } from "@shared/helpers/sql-relations";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useCallback } from "react";
import CustomFilter from "./CustomFilter";
import type { EntityFilterProps } from "./helpers";
import { getProcess, SELECT_OPERATORS } from "./helpers";

dayjs.extend(utc);

type DateFilterProps<T> = EntityFilterProps<T>

const DateFilter = <T extends any>(props: DateFilterProps<T>) => {
  const { filter, closeMenu } = props;
  const { getEntity } = useHyperTable<T>();

  const handleSave = useCallback(
    (
      onSave: RenderFilterProps<T>["onSave"],
      value: Date | null | undefined,
      selectValue: string | undefined,
    ) => {
      const entity = getEntity();
      const isRelationalField =
        "hyperFilter" in entity && filter.fieldName.includes(".");
      onSave(
        (prev) => {
          return {
            meta: {
              ...prev.meta,
              date: value ? value.toISOString() : undefined,
            },
            value: value
              ? isRelationalField
                ? (entity as any).hyperFilter(
                    generateHyperFilterFromPath(
                      entity,
                      filter.fieldName,
                      getProcess("date", selectValue)(value),
                    ),
                  )
                : {
                    ...prev.value,
                    [filter.fieldName]: getProcess("date", selectValue)(value),
                  }
              : {},
          };
        },
        { closeOnSave: false },
      );
    },
    [getEntity(), getProcess, filter.fieldName],
  );

  return (
    <CustomFilter<T>
      closeMenu={closeMenu}
      filter={{
        ...filter,
        icon: "date",
        select: {
          initialValue: SELECT_OPERATORS.date[0].value,
          options: SELECT_OPERATORS.date.map((v) => ({
            value: v.value,
            caption: v.caption,
          })),
          onChange: ({ meta, onSave, selectValue }) => {
            handleSave(
              onSave,
              typeof meta.date === "string"
                ? dayjs(meta.date).toDate()
                : undefined,
              selectValue,
            );
          },
        },
        renderFilter: useCallback(
          ({ meta, onSave, selectValue }) => (
            <MenuItem
              autoFocus
              sx={{
                ["&.Mui-focusVisible"]: { backgroundColor: "transparent" },
              }}
              disableRipple
              disableTouchRipple
            >
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                localeText={
                  heIL.components.MuiLocalizationProvider.defaultProps
                    .localeText
                }
                adapterLocale="he"
              >
                <DatePicker
                  name={filter.fieldName as string}
                  timezone="UTC"
                  format="DD/MM/YYYY"
                  onChange={(v) => handleSave(onSave, v?.toDate(), selectValue)}
                  value={
                    typeof meta.date === "string" ? dayjs(meta.date) : null
                  }
                  reduceAnimations
                  autoFocus
                />
              </LocalizationProvider>
            </MenuItem>
          ),
          [],
        ),
      }}
    />
  );
};

export default DateFilter;
